/**
 * MemberManager Component
 * Modal for managing class members
 */

import React, { useEffect, useState } from 'react';
import type { Class, ClassMember, Student } from '@shared/types/teach';
import { X, Search, Loader2, UserMinus, UserPlus } from 'lucide-react';
import { useTeachStore } from '../../stores/teachStore';
import { MessageDialog } from '../common/MessageDialog';

interface MemberManagerProps {
  class: Class;
  onClose: () => void;
}

export const MemberManager: React.FC<MemberManagerProps> = ({
  class: cls,
  onClose,
}) => {
  const {
    classMembers,
    students,
    membersLoading,
    studentsLoading,
    fetchClassMembers,
    fetchStudents,
    addMembersToClass,
    removeMemberFromClass,
  } = useTeachStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  // Message dialog state
  const [messageDialog, setMessageDialog] = useState<{
    isOpen: boolean;
    title?: string;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  const showMessage = (message: string, type: 'success' | 'error' | 'info' = 'info', title?: string) => {
    setMessageDialog({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeMessageDialog = () => {
    setMessageDialog(prev => ({ ...prev, isOpen: false }));
  };

  // Load members and all students
  useEffect(() => {
    fetchClassMembers(cls.id);
    fetchStudents({ pageSize: 1000 }); // Load all students
  }, [cls.id]);

  // Get available students (not in this class)
  const availableStudents = students.filter(
    (student) => !classMembers.some((member) => member.id === student.id)
  );

  // Filter available students by search query
  const filteredAvailableStudents = availableStudents.filter((student) =>
    student.nickname.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.phone.includes(searchQuery)
  );

  const handleSelectStudent = (studentId: string, event: React.MouseEvent) => {
    const newSelected = new Set(selectedStudents);

    if (event.ctrlKey || event.metaKey) {
      // Ctrl/Cmd + click: toggle selection
      if (newSelected.has(studentId)) {
        newSelected.delete(studentId);
      } else {
        newSelected.add(studentId);
      }
    } else {
      // Regular click: single selection
      newSelected.clear();
      newSelected.add(studentId);
    }

    setSelectedStudents(newSelected);
  };

  const handleAddMembers = async () => {
    if (selectedStudents.size === 0) return;

    setLoading(true);
    try {
      await addMembersToClass(cls.id, Array.from(selectedStudents));
      setSelectedStudents(new Set());
      showMessage(`成功添加 ${selectedStudents.size} 名学生`, 'success');
    } catch (error: any) {
      showMessage(error.message || '添加学生失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (studentId: string) => {
    setLoading(true);
    try {
      await removeMemberFromClass(cls.id, studentId);
      showMessage('学生已移除', 'success');
    } catch (error: any) {
      showMessage(error.message || '移除学生失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            管理班级成员 - {cls.name}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
            {/* Left: Current members */}
            <div className="flex flex-col">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                当前成员 ({classMembers.length})
              </h3>
              <div className="flex-1 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                {membersLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                  </div>
                ) : classMembers.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                    暂无成员
                  </div>
                ) : (
                  <div className="overflow-y-auto h-full">
                    {classMembers.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                      >
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {member.nickname}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {member.grade} · 加入于 {formatDate(member.joinedAt)}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRemoveMember(member.id)}
                          disabled={loading}
                          className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                          title="移除"
                        >
                          <UserMinus className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right: Available students */}
            <div className="flex flex-col">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                添加学生
              </h3>

              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="搜索学生"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Available students list */}
              <div className="flex-1 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mb-4">
                {studentsLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                  </div>
                ) : filteredAvailableStudents.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                    {searchQuery ? '未找到匹配的学生' : '所有学生都已加入'}
                  </div>
                ) : (
                  <div className="overflow-y-auto h-full">
                    {filteredAvailableStudents.map((student) => (
                      <div
                        key={student.id}
                        onClick={(e) => handleSelectStudent(student.id, e)}
                        className={`p-4 cursor-pointer border-b border-gray-200 dark:border-gray-700 last:border-b-0 transition-colors ${
                          selectedStudents.has(student.id)
                            ? 'bg-blue-50 dark:bg-blue-900/20'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <p className="font-medium text-gray-900 dark:text-white">
                          {student.nickname}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {student.grade}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add button */}
              <button
                onClick={handleAddMembers}
                disabled={selectedStudents.size === 0 || loading}
                className="w-full inline-flex items-center justify-center px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                {loading ? '添加中...' : `添加选中学生 (${selectedStudents.size})`}
              </button>

              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                按住 Ctrl/Cmd 键可多选
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            关闭
          </button>
        </div>
      </div>

      {/* Message dialog */}
      <MessageDialog
        isOpen={messageDialog.isOpen}
        title={messageDialog.title}
        message={messageDialog.message}
        type={messageDialog.type}
        onClose={closeMessageDialog}
      />
    </div>
  );
};
